/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { connect } from 'react-redux'

import FormGroup from 'live/components/forms/ui/form-group'
import GlobalAlert from 'live/components/ui/global-alert'
import LiveModal from 'live/components/ui/Modal'
import userLocalStorage from 'live/lib/user-local-storage'

import {
    convertUnits,
    exportSelectedRules,
    getAssetRules,
    getCurves,
    importFile,
    loadRigStateOutputs,
    setGlobalAlert,
    toggleSelected,
    updateAssetId,
    updateRule
} from '../../actions'
import { editorColumns as columns, CONFIG_UNIT_SET_LOCAL_STORAGE_PATH, UPDATE_SOUND_BROADCAST } from '../../constants'
import {
    Column,
    Columns,
    Empty,
    Header,
    ListContainer,
    StyledBox,
    StyledBoxContent,
    StyledBoxTitle,
    Title
} from '../styles'
import Rule from './Rule'
import ListButtons from './ListButtons'

const EditorPage = ({
    dashboardUnitSet,
    selected: selectedAsset,
    sideview,

    allRules,
    assetId,
    broadcastChannel,
    curves,
    globalAlert,
    loading,
    selectedIndices,

    convertUnits,
    exportSelectedRules,
    getAssetRules,
    getCurves,
    importFile,
    loadRigStateOutputs,
    setGlobalAlert,
    toggleSelected,
    updateAssetId,
    updateRule
}) => {
    const asset = selectedAsset?.asset

    const selectedUnitSetId = asset?.state?.unit_set?.data ?? -1
    const [loadingUnitSet, setLoadingUnitSet] = useState(false)
    const [selectedUnitSet, setSelectedUnitSet] = useState({ id: selectedUnitSetId })

    const [files, setFiles] = useState([])
    const uploadRef = useRef(null)

    const [showExportFilenameModal, toggleExportFilenameModal] = useState(false)
    const [exportFilename, setExportFilename] = useState('alarm-rules-' + assetId + '.json')

    useEffect(() => {
        loadRigStateOutputs()
    }, [loadRigStateOutputs])

    useEffect(() => {
        if (!sideview && (asset?.id || asset.id === 0) && !curves) {
            const abortController = new AbortController()
            getCurves(abortController)

            return () => {
                abortController.abort()
            }
        }
    }, [asset.id, curves, getCurves, sideview])

    useEffect(() => {
        if (!sideview && (asset?.id || asset?.id === 0)) {
            updateAssetId(asset.id)
        }
    }, [asset.id, sideview, updateAssetId])

    useEffect(() => {
        if (!sideview && curves) {
            const abortController = new AbortController()
            getAssetRules(null, abortController)

            return () => {
                abortController.abort()
            }
        }
    }, [getAssetRules, asset?.id, curves, sideview])

    useEffect(() => {
        const abortController = new AbortController()

        const getUnitSet = async () => {
            let unitSet

            if (parseInt(selectedUnitSetId) < 0) {
                unitSet = { id: parseInt(selectedUnitSetId), data: {} }
            } else {
                const response = await fetch(`/services/plugin-assets/display-units/${selectedUnitSetId}`, {
                    signal: abortController.signal
                })
                if (!response.ok) throw new Error('Could not get data for display unit set ' + selectedUnitSetId)

                unitSet = await response.json()
            }

            setSelectedUnitSet(unitSet)
            userLocalStorage.setItem(CONFIG_UNIT_SET_LOCAL_STORAGE_PATH, selectedUnitSetId)
            await convertUnits(unitSet)
        }

        if (!sideview && !loading && curves) {
            setLoadingUnitSet(true)
            getUnitSet()
                .catch(err => {
                    if (err.name !== 'AbortError') {
                        console.error('Error getting unit set data:', err)
                    }
                })
                .finally(() => setLoadingUnitSet(false))
        }

        return () => {
            abortController.abort()
        }
    }, [selectedUnitSetId, convertUnits, curves, sideview, loading])

    useEffect(() => {
        broadcastChannel.onmessage = msg => {
            if (msg.type === UPDATE_SOUND_BROADCAST) {
                if (msg.assetId === assetId && msg.status && Object.entries(msg.status).length) {
                    Object.entries(msg.status).forEach(([id, sound]) => {
                        const index = allRules.find(r => r.id === id)?.index
                        if (typeof index !== 'undefined') {
                            updateRule(index, { sound })
                        }
                    })
                }
            }
        }
    }, [allRules, assetId, broadcastChannel, updateRule])

    return (
        <div style={{ height: '100%' }}>
            <input
                type="file"
                ref={uploadRef}
                id="alarm-import-upload"
                value={files}
                onChange={e => importFile(e, setFiles, sideview ? dashboardUnitSet : selectedUnitSet, sideview)}
                style={{ display: 'none' }}
            />

            {ReactDOM.createPortal(
                <GlobalAlert
                    allowClose={true}
                    className={`alert-sm ${globalAlert.success ? 'success' : 'error'}`}
                    message={globalAlert.message}
                    onClose={() => setGlobalAlert('', true)}
                    show={!!globalAlert.message}
                    visibleTime={6}
                />,
                document.body
            )}

            {showExportFilenameModal && (
                <LiveModal
                    show={showExportFilenameModal}
                    onHide={() => toggleExportFilenameModal(false)}
                    className="modal-xs"
                    animation={false}
                >
                    <LiveModal.Header>
                        <LiveModal.Title>Export alarm rules</LiveModal.Title>
                    </LiveModal.Header>

                    <LiveModal.Body>
                        <FormGroup
                            type="text"
                            label="Select the name of the exported file"
                            value={exportFilename}
                            onChange={e => setExportFilename(e.target.value)}
                        />
                    </LiveModal.Body>

                    <LiveModal.Footer>
                        <LiveModal.ModalActions
                            submitText="Export"
                            cancelText="cancel"
                            onSubmit={() => {
                                exportSelectedRules(exportFilename)
                                toggleExportFilenameModal(false)
                                setExportFilename('alarm-rules-' + assetId + '.json')
                            }}
                            onCancel={() => toggleExportFilenameModal(false)}
                        />
                    </LiveModal.Footer>
                </LiveModal>
            )}

            {/* <div style={{ display: 'flex' }}>
                <StyledBox>
                    <StyledBoxTitle>Total Alarms</StyledBoxTitle>
                    <StyledBoxContent>{allRules.length}</StyledBoxContent>
                </StyledBox>
                <StyledBox>
                    <StyledBoxTitle>Active Alarms</StyledBoxTitle>
                    <StyledBoxContent>{allRules.filter(r => r.active).length}</StyledBoxContent>
                </StyledBox>
                <StyledBox>
                    <StyledBoxTitle>Alarming Now</StyledBoxTitle>
                    <StyledBoxContent>{alarms.length}</StyledBoxContent>
                </StyledBox>
            </div> */}

            {sideview ? null : <Title>Alarms list</Title>}
            <ListButtons
                sideview={sideview}
                onUpload={() => uploadRef.current && uploadRef.current.click()}
                showExportFilenameModal={() => toggleExportFilenameModal(true)}
            />

            <Header>
                <Columns>
                    <Column style={columns[0]}>
                        <input
                            type={'checkbox'}
                            onChange={() =>
                                toggleSelected(
                                    allRules.map((_, idx) => idx),
                                    true
                                )
                            }
                            checked={selectedIndices.size && selectedIndices.size === allRules.length}
                        />
                    </Column>
                    <Column style={columns[1]} className="selected">
                        description
                    </Column>
                    <Column style={columns[2]}>active</Column>
                    <Column style={columns[3]}>type</Column>
                    <Column style={columns[4]}>sound</Column>
                    <Column style={columns[5]}>severity</Column>
                    <Column style={columns[6]} />
                    <Column style={{ ...columns[7], marginRight: 14 }} />
                </Columns>
            </Header>

            <ListContainer>
                {asset?.id !== assetId || loading || loadingUnitSet ? (
                    <div className="loading" />
                ) : allRules.length ? (
                    allRules.map((rule, i) => (
                        <Rule
                            key={i}
                            sideview={sideview}
                            rule={rule}
                            selectedUnitSet={sideview ? dashboardUnitSet : selectedUnitSet}
                        />
                    ))
                ) : (
                    <Empty>No alarms configured</Empty>
                )}
            </ListContainer>
        </div>
    )
}

function mapStateToProps({
    editorReducer: {
        assetId,
        broadcastChannel,
        curves,
        globalAlert,
        loading,
        localRules: allRules,
        selected: selectedIndices
    }
}) {
    return {
        allRules,
        assetId,
        broadcastChannel,
        curves,
        globalAlert,
        loading,
        selectedIndices
    }
}

export default connect(mapStateToProps, {
    convertUnits,
    exportSelectedRules,
    getAssetRules,
    getCurves,
    importFile,
    loadRigStateOutputs,
    setGlobalAlert,
    toggleSelected,
    updateAssetId,
    updateRule
})(EditorPage)
